import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import ProductsDetail from '../deal/ProductsDetail'
import { CheckCircleOutline } from '@mui/icons-material'
import Tooltip from '../../../common/Tooltip'

const CloseDealModal = ({
  opportunity,
  isOpen,
  productTypes,
  onCancel,
  onSave,
  updateProducts,
  dropdowns,
  billingCycleTypes,
}) => {
  const [closedDate, setClosedDate] = useState(
    new Date().toLocaleDateString('sv')
  )
  const [outcomeReason, setOutcomeReason] = useState(null)
  const [nextStep, setNextStep] = useState(null)

  const [wonOrLost, setWonOrLost] = useState(null)

  useEffect(() => {
    setWonOrLost(null)
  }, [isOpen])

  useEffect(() => {
    setOutcomeReason(null)
  }, [wonOrLost])

  const wonOutcomeReasonOptions =
    dropdowns?.find((dd) => dd.key === 'wonOutcomeReason')?.options || []
  const lostOutcomeReasonOptions =
    dropdowns?.find((dd) => dd.key === 'lostOutcomeReason')?.options || []

  const nextStepOptions =
    dropdowns?.find((dd) => dd.key === 'nextStep')?.options || []

  // const recurringProductIds = productTypes
  //   // MAGIC-NUMBER: billingCycleTypeId 1 === 'One time'
  //   .filter((p) => p.billingCycleTypeId !== 1)
  //   .map((p) => p.id)

  const hasAllStartDates = opportunity?.products?.every((p) => !!p.startDate)

  const canSubmit =
    (hasAllStartDates || wonOrLost === 'lost') && !!outcomeReason && !!nextStep

  const closedWonContent = () => {
    return (
      <>
        <DialogTitle>
          Congratulations! It looks like you've closed a deal.
        </DialogTitle>
        <DialogContent>
          <Box>{wonOrLostRadio()}</Box>
          <TextField
            required
            sx={{ minWidth: '300px', mr: 2 }}
            label={'Closed date'}
            type={'date'}
            margin='dense'
            InputLabelProps={{
              shrink: true,
            }}
            value={closedDate}
            onChange={(e) => {
              setClosedDate(e.target.value)
            }}
          ></TextField>

          <TextField
            required
            sx={{ minWidth: '300px', mr: 2 }}
            margin='dense'
            label={'Outcome reason'}
            value={outcomeReason || ''}
            onChange={(e) => setOutcomeReason(e.target.value)}
            select
          >
            <MenuItem value={''}>None</MenuItem>
            {wonOutcomeReasonOptions
              .filter((o) => !!o.name)
              .map((o, i) => (
                <MenuItem key={i} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            required
            sx={{ minWidth: '300px' }}
            margin='dense'
            label={'Next Step'}
            value={nextStep || ''}
            onChange={(e) => setNextStep(e.target.value)}
            select
          >
            <MenuItem value={''}>None</MenuItem>
            {nextStepOptions
              .filter((o) => !!o.name)
              .map((o, i) => (
                <MenuItem key={i} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
          </TextField>

          <Typography sx={{ mt: 2 }}>
            <b>
              Double check the products that the customer is purchasing and add
              payment dates
            </b>
          </Typography>
          <ProductsDetail
            productTypes={productTypes || []}
            updateProducts={updateProducts}
            products={opportunity.products || []}
            includeDates
            billingCycleTypes={billingCycleTypes}
            tooltip={
              <Tooltip
                textProps={{ sx: { whiteSpace: 'pre-line', fontSize: '14px' } }}
                infoText={`You can validate Products & Services in this section, specifying the quantity, price, billing cycle, and payment due dates.

                Note: The payment "Start Date" indicates when the payment for the product or service is due.
                If you set a future "Start Date" for a monthly, quarterly, or annual billing cycle, the revenue will be labeled as "Potential Revenue" until the actual due date.
                On the day of the "due date" it will then automatically update to "Recurring Revenue" in the account profile.`}
              />
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              onSave({
                nextStep,
                outcomeReason,
                closedDate,
                wonOrLost,
              })
            }
            variant={'contained'}
            endIcon={<CheckCircleOutline />}
          >
            Close Deal
          </Button>
        </DialogActions>
      </>
    )
  }

  const closedLostContent = () => {
    return (
      <>
        <DialogTitle>Lost deal</DialogTitle>
        <DialogContent>
          <Box>{wonOrLostRadio()}</Box>
          <TextField
            required
            sx={{ minWidth: '300px', mr: 2 }}
            label={'Closed date'}
            type={'date'}
            margin='dense'
            InputLabelProps={{
              shrink: true,
            }}
            value={closedDate}
            onChange={(e) => {
              setClosedDate(e.target.value)
            }}
          ></TextField>
          <TextField
            required
            sx={{ minWidth: '300px', mr: 2 }}
            margin='dense'
            label={'Outcome reason'}
            value={outcomeReason || ''}
            onChange={(e) => setOutcomeReason(e.target.value)}
            select
          >
            <MenuItem value={''}>None</MenuItem>
            {lostOutcomeReasonOptions
              .filter((o) => !!o.name)
              .map((o, i) => (
                <MenuItem key={i} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
          </TextField>

          <TextField
            required
            sx={{ minWidth: '300px' }}
            margin='dense'
            label={'Next Step'}
            value={nextStep || ''}
            onChange={(e) => setNextStep(e.target.value)}
            select
          >
            <MenuItem value={''}>None</MenuItem>
            {nextStepOptions
              .filter((o) => !!o.name)
              .map((o, i) => (
                <MenuItem key={i} value={o.id}>
                  {o.name}
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>

        <DialogActions>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            disabled={!canSubmit}
            onClick={() =>
              onSave({
                nextStep,
                outcomeReason,
                closedDate,
                wonOrLost,
              })
            }
            variant={'contained'}
            endIcon={<CheckCircleOutline />}
          >
            Close Deal
          </Button>
        </DialogActions>
      </>
    )
  }

  const wonOrLostRadio = () => {
    return (
      <FormControl>
        <FormLabel>Won or Lost</FormLabel>
        <RadioGroup
          onChange={(e) => setWonOrLost(e.target.value)}
          value={wonOrLost}
        >
          <FormControlLabel value='won' control={<Radio />} label='Won' />
          <FormControlLabel value='lost' control={<Radio />} label='Lost' />
        </RadioGroup>
      </FormControl>
    )
  }

  return (
    <Dialog open={isOpen} fullWidth maxWidth={'xl'}>
      {wonOrLost === null && (
        <>
          <DialogTitle>Close deal</DialogTitle>
          <DialogContent>{wonOrLostRadio()}</DialogContent>
          <DialogActions>
            <Button onClick={onCancel}>Cancel</Button>
          </DialogActions>
        </>
      )}
      {wonOrLost === 'won' && closedWonContent()}
      {wonOrLost === 'lost' && closedLostContent()}
    </Dialog>
  )
}

export default CloseDealModal
